// Intelligent Incentive Matching Service
// This service connects to Supabase and performs project-specific matching

import { dbHelpers } from './supabase';

// Project content analysis helper
export const analyzeProjectContent = (project, budget) => {
  return {
    title: project.working_title || 'Untitled Project',
    genre: project.genre || [],
    productionType: project.production_type || 'feature',
    settingCountries: project.setting_countries || [],
    settingCities: project.setting_cities || [],
    period: project.period_years ? `${project.period_years.start}-${project.period_years.end}` : 'contemporary',
    language: project.primary_language || 'English',
    runtime: project.expected_runtime_minutes || 120,
    hasMinors: project.presence_of_minors || false,
    vfxIntensity: project.stunts_or_vfx_intensity || 'medium',
    localSpendPercent: project.likely_spend_split_local_percent || 60,
    budget: budget || 1000000, // Default to $1M
    budgetUSD: `$${((budget || 1000000) / 1000000).toFixed(1)}M USD`
  };
};

// Smart matching algorithm that scores incentives based on project characteristics
export const scoreIncentiveForProject = (incentive, projectContent) => {
  let score = 0;
  let reasoning = [];

  try {
    // BASE SCORE FROM INCENTIVE QUALITY (40 points max) - This is the PRIMARY factor
    const qualityScore = (incentive.mark_stars || 3) * 10; // 3 stars = 30, 5 stars = 50
    score += qualityScore;

    if (incentive.mark_stars >= 4.5) {
      reasoning.push(`⭐ EXCELLENT (${incentive.mark_stars}/5 stars) - Premium incentive program`);
    } else if (incentive.mark_stars >= 4.0) {
      reasoning.push(`⭐ VERY GOOD (${incentive.mark_stars}/5 stars) - Highly rated program`);
    } else if (incentive.mark_stars >= 3.5) {
      reasoning.push(`⭐ GOOD (${incentive.mark_stars}/5 stars) - Solid incentive program`);
    }

    // 1. GENRE MATCHING (15 points max)
    if (projectContent.genre && projectContent.genre.length > 0) {
      const genreMatch = projectContent.genre.some(g => {
        if (typeof g === 'string' && typeof incentive.summary_one_liner === 'string') {
          return incentive.summary_one_liner.toLowerCase().includes(g.toLowerCase());
        }
        return false;
      });
      if (genreMatch) {
        score += 15;
        reasoning.push(`📽️ Genre alignment with ${projectContent.genre.join(', ')}`);
      }
    }

    // 2. LOCATION/JURISDICTION MATCHING (25 points max)
    if (projectContent.settingCountries && projectContent.settingCountries.length > 0) {
      const locationMatch = projectContent.settingCountries.some(country => {
        if (typeof country === 'string' && typeof incentive.jurisdiction_name === 'string') {
          // Direct country match
          if (incentive.jurisdiction_name.toLowerCase().includes(country.toLowerCase())) {
            return true;
          }
          // Handle specific cases
          if (country.toLowerCase().includes('uk') || country.toLowerCase().includes('united kingdom')) {
            return incentive.jurisdiction_name.toLowerCase().includes('united kingdom') || 
                   incentive.jurisdiction_name.toLowerCase().includes('england') ||
                   incentive.jurisdiction_name.toLowerCase().includes('scotland') ||
                   incentive.jurisdiction_name.toLowerCase().includes('wales');
          }
          if (country.toLowerCase().includes('us') || country.toLowerCase().includes('united states')) {
            return incentive.jurisdiction_name.toLowerCase().includes('(us)') ||
                   incentive.jurisdiction_name.toLowerCase().includes('georgia') ||
                   incentive.jurisdiction_name.toLowerCase().includes('california') ||
                   incentive.jurisdiction_name.toLowerCase().includes('new york');
          }
        }
        return false;
      });
      
      if (locationMatch) {
        score += 20;
        reasoning.push(`📍 Location match for ${projectContent.settingCountries.join(', ')}`);
      }
    }

    // 3. BUDGET TIER MATCHING (15 points max)
    const budget = projectContent.budget;
    if (budget) {
      // Check minimum spend requirements
      const minSpendMatch = checkMinimumSpendRequirement(incentive, budget);
      if (minSpendMatch.eligible) {
        score += 12;
        reasoning.push(`💰 Budget meets minimum spend requirements`);
      } else if (minSpendMatch.reason) {
        score -= 5; // Reduced penalty
        reasoning.push(`⚠️ ${minSpendMatch.reason}`);
      }
    }

    // 4. PRODUCTION TYPE MATCHING (10 points max)
    if (projectContent.productionType) {
      const typeMatch = checkProductionTypeMatch(incentive, projectContent.productionType);
      if (typeMatch) {
        score += 8;
        reasoning.push(`🎬 Supports ${projectContent.productionType} productions`);
      }
    }

    // 5. LANGUAGE/CULTURAL REQUIREMENTS (10 points max)
    if (projectContent.language) {
      const languageBonus = checkLanguageRequirements(incentive, projectContent.language);
      if (languageBonus > 0) {
        score += languageBonus;
        reasoning.push(`🌐 Language/cultural alignment`);
      }
    }

    // 6. SPECIAL CONSIDERATIONS (5 points each)
    
    // VFX/Technical requirements
    if (projectContent.vfxIntensity === 'high') {
      const hasVFXSupport = checkForVFXSupport(incentive);
      if (hasVFXSupport) {
        score += 5;
        reasoning.push('🎭 Enhanced VFX/technical support');
      }
    }

    // Minor actors
    if (projectContent.hasMinors) {
      const hasMinorSupport = checkForMinorSupport(incentive);
      if (hasMinorSupport) {
        score += 5;
        reasoning.push('👶 Supports productions with child actors');
      }
    }

    // Runtime considerations
    if (projectContent.runtime) {
      const runtimeBonus = checkRuntimeRequirements(incentive, projectContent.runtime);
      if (runtimeBonus > 0) {
        score += runtimeBonus;
        reasoning.push('Runtime requirements met');
      }
    }

    // 7. FINANCIAL ATTRACTIVENESS (calculated separately)
    const financialScore = calculateFinancialScore(incentive, projectContent);
    score += financialScore.score;
    if (financialScore.reasoning) {
      reasoning.push(financialScore.reasoning);
    }

  } catch (error) {
    console.warn('Error scoring incentive:', incentive.jurisdiction_name, error);
  }

  return {
    score: Math.max(0, Math.min(100, score)),
    reasoning: reasoning,
    maxPossibleScore: 100
  };
};

// Helper functions for specific matching criteria

const checkMinimumSpendRequirement = (incentive, budget) => {
  try {
    const tooltip = incentive.tooltip;
    if (tooltip && typeof tooltip === 'object' && tooltip.min_spend) {
      const minSpendStr = tooltip.min_spend.toString();
      
      // Extract numeric value from min_spend string
      const minSpendMatch = minSpendStr.match(/[\d,]+/);
      if (minSpendMatch) {
        const minSpend = parseInt(minSpendMatch[0].replace(/,/g, ''));
        
        // Convert to USD if needed (basic currency detection)
        let minSpendUSD = minSpend;
        if (minSpendStr.includes('£')) {
          minSpendUSD = minSpend * 1.27; // GBP to USD approximate
        } else if (minSpendStr.includes('€')) {
          minSpendUSD = minSpend * 1.10; // EUR to USD approximate
        } else if (minSpendStr.includes('C$')) {
          minSpendUSD = minSpend * 0.74; // CAD to USD approximate
        } else if (minSpendStr.includes('A$')) {
          minSpendUSD = minSpend * 0.67; // AUD to USD approximate
        }
        
        if (budget >= minSpendUSD) {
          return { eligible: true };
        } else {
          return { 
            eligible: false, 
            reason: `Minimum spend requirement: ${tooltip.min_spend}` 
          };
        }
      }
    }
    return { eligible: true }; // No minimum requirement found
  } catch (error) {
    return { eligible: true }; // Default to eligible if can't parse
  }
};

const checkProductionTypeMatch = (incentive, productionType) => {
  const summary = incentive.summary_one_liner || '';
  const lowerSummary = summary.toLowerCase();
  const lowerType = productionType.toLowerCase();
  
  // Direct matches
  if (lowerSummary.includes(lowerType)) return true;
  
  // Type-specific matches
  if (lowerType === 'feature') {
    return lowerSummary.includes('film') || lowerSummary.includes('movie') || lowerSummary.includes('feature');
  }
  if (lowerType === 'tv' || lowerType === 'television') {
    return lowerSummary.includes('tv') || lowerSummary.includes('television') || lowerSummary.includes('series');
  }
  if (lowerType === 'documentary') {
    return lowerSummary.includes('documentary') || lowerSummary.includes('doc');
  }
  
  return false;
};

const checkLanguageRequirements = (incentive, language) => {
  const summary = incentive.summary_one_liner || '';
  const tooltip = incentive.tooltip || {};
  
  // Check for cultural test requirements (often favor local language)
  if (tooltip.cultural_test === true) {
    // English productions often qualify for many cultural tests
    if (language.toLowerCase() === 'english') {
      return 5;
    }
    // Local language might get bonus
    return 3;
  }
  
  return 0;
};

const checkForVFXSupport = (incentive) => {
  const summary = (incentive.summary_one_liner || '').toLowerCase();
  const tooltipStr = JSON.stringify(incentive.tooltip || {}).toLowerCase();
  
  return summary.includes('vfx') || 
         summary.includes('visual effects') || 
         summary.includes('post-production') ||
         tooltipStr.includes('vfx') ||
         tooltipStr.includes('visual') ||
         summary.includes('digital') ||
         summary.includes('animation');
};

const checkForMinorSupport = (incentive) => {
  const summary = (incentive.summary_one_liner || '').toLowerCase();
  const tooltipStr = JSON.stringify(incentive.tooltip || {}).toLowerCase();
  
  return summary.includes('minor') || 
         summary.includes('child') || 
         summary.includes('youth') ||
         tooltipStr.includes('minor') ||
         tooltipStr.includes('child');
};

const checkRuntimeRequirements = (incentive, runtime) => {
  // Most incentives don't have specific runtime requirements
  // But some might favor longer form content
  if (runtime >= 90) { // Feature length
    return 2;
  }
  return 0;
};

const calculateFinancialScore = (incentive, projectContent) => {
  const budget = projectContent.budget;
  const localSpendPercent = projectContent.localSpendPercent;
  const baseRate = incentive.base_rate_percent || 0;
  
  if (!budget) return { score: 0 };
  
  const localSpendAmount = budget * (localSpendPercent / 100);
  
  // Calculate potential savings
  let potentialSavings = localSpendAmount * (baseRate / 100);
  
  // Check for caps
  try {
    const tooltip = incentive.tooltip;
    if (tooltip && tooltip.cap && tooltip.cap !== 'No cap') {
      const capStr = tooltip.cap.toString();
      const capMatch = capStr.match(/[\d,]+/);
      if (capMatch) {
        const cap = parseInt(capMatch[0].replace(/,/g, ''));
        let capUSD = cap;
        
        // Convert currency caps to USD
        if (capStr.includes('£')) capUSD = cap * 1.27;
        else if (capStr.includes('€')) capUSD = cap * 1.10;
        else if (capStr.includes('C$')) capUSD = cap * 0.74;
        else if (capStr.includes('A$')) capUSD = cap * 0.67;
        
        potentialSavings = Math.min(potentialSavings, capUSD);
      }
    }
  } catch (error) {
    // Continue with uncapped calculation
  }
  
  // Score based on potential savings as percentage of budget
  const savingsPercent = (potentialSavings / budget) * 100;
  
  if (savingsPercent >= 25) return { score: 20, reasoning: 'Excellent financial return (25%+)' };
  if (savingsPercent >= 20) return { score: 16, reasoning: 'Very good financial return (20%+)' };
  if (savingsPercent >= 15) return { score: 12, reasoning: 'Good financial return (15%+)' };
  if (savingsPercent >= 10) return { score: 8, reasoning: 'Moderate financial return (10%+)' };
  if (savingsPercent >= 5) return { score: 4, reasoning: 'Basic financial return (5%+)' };
  
  return { score: 0 };
};

// Main matching function that fetches from database and ranks incentives
export const matchIncentivesToProject = async (submissionData) => {
  try {
    console.log('🔥 Starting matchIncentivesToProject with submission:', submissionData?.id);
    
    // Get all incentives from the database
    const allIncentives = await dbHelpers.getAllIncentives();
    
    if (!allIncentives || allIncentives.length === 0) {
      console.error('❌ No incentives found in database - this is the main problem!');
      throw new Error('No incentives found in database');
    }

    console.log(`🎯 Found ${allIncentives.length} incentives in database - proceeding with intelligent matching`);

    // Analyze project content
    const projectContent = analyzeProjectContent(
      submissionData.parsed_metadata || {}, 
      submissionData.budget_amount
    );

    console.log('🔍 Project analysis:', projectContent);
    console.log('📊 Sample incentives from database:', allIncentives.slice(0, 3).map(i => ({
      name: i.jurisdiction_name || i.jurisdiction,
      stars: i.mark_stars || 'N/A',
      rate: i.base_rate_percent || i.value,
      type: i.incentive_type || i.type
    })));

    // Score each incentive for this project
    const scoredIncentives = allIncentives.map(incentive => {
      const scoring = scoreIncentiveForProject(incentive, projectContent);
      
      // Calculate estimated savings - FIXED CALCULATION WITH DEBUGGING
      const budget = projectContent.budget || 1000000; // Default to $1M not $10M
      const localSpendPercent = projectContent.localSpendPercent || 60;
      const localSpendAmount = budget * (localSpendPercent / 100);
      const rate = Math.min(incentive.base_rate_percent || 0, 50); // Cap rate at 50%
      let estimatedSavings = Math.round(localSpendAmount * (rate / 100));

      // Calculation complete
      
      // Calculation is now working correctly
      
      // Apply caps if they exist
      try {
        const tooltip = incentive.tooltip;
        if (tooltip && tooltip.cap && tooltip.cap !== 'No cap') {
          const capStr = tooltip.cap.toString();
          // Improved cap parsing to handle millions and decimals
          const capMatch = capStr.match(/[\d,]+(?:\.\d+)?/);
          if (capMatch) {
            let cap = parseFloat(capMatch[0].replace(/,/g, ''));

            // Handle million amounts (21.4m = 21,400,000)
            if (capStr.toLowerCase().includes('m') && cap < 1000) {
              cap = cap * 1000000;
            } else if (capStr.toLowerCase().includes('k') && cap < 1000000) {
              cap = cap * 1000;
            }

            let capUSD = cap;

            // Currency conversion
            if (capStr.includes('£')) capUSD = cap * 1.27;
            else if (capStr.includes('€')) capUSD = cap * 1.10;
            else if (capStr.includes('C$')) capUSD = cap * 0.74;
            else if (capStr.includes('A$')) capUSD = cap * 0.67;

            // DEBUG: Log cap application for Italy
            if (incentive.jurisdiction_name?.includes('Italy')) {
              console.log('🍝 ITALY CAP CHECK (FIXED):', {
                capStr,
                parsedCap: cap,
                capUSD,
                beforeCap: estimatedSavings,
                afterCap: Math.min(estimatedSavings, capUSD)
              });
            }

            estimatedSavings = Math.min(estimatedSavings, capUSD);
          }
        }
      } catch (error) {
        // Keep original calculation
      }

      // Return matched incentive with calculated savings

      return {
        ...incentive,
        matchScore: scoring.score,
        matchReasoning: scoring.reasoning,
        estimatedSavings: estimatedSavings, // FIXED: Use camelCase property name
        estimated_savings: estimatedSavings, // Keep snake_case for DB compatibility
        currency: submissionData.budget_currency || 'USD'
      };
    });

    // Sort by match score (descending), then by estimated savings
    const rankedIncentives = scoredIncentives
      .filter(incentive => incentive.matchScore > 15) // Only show reasonably good matches
      .sort((a, b) => {
        // Primary sort: Star rating (highest first) - BEST INCENTIVES AT TOP
        if (b.mark_stars !== a.mark_stars) {
          return b.mark_stars - a.mark_stars;
        }
        // Secondary sort: Match score
        if (b.matchScore !== a.matchScore) {
          return b.matchScore - a.matchScore;
        }
        // Tertiary sort: Estimated savings
        return b.estimated_savings - a.estimated_savings;
      })
      .slice(0, 10) // Top 10 matches
      .map((incentive, index) => ({
        ...incentive,
        rank: index + 1
      }));

    console.log(`Returning ${rankedIncentives.length} ranked incentives`);
    console.log('Top matches:', rankedIncentives.slice(0, 3).map(i => ({
      name: i.jurisdiction_name,
      score: i.matchScore,
      savings: i.estimated_savings,
      reasoning: i.matchReasoning
    })));

    return rankedIncentives;

  } catch (error) {
    console.error('Error matching incentives:', error);
    throw error;
  }
};

// Export for use in other services
export default {
  matchIncentivesToProject,
  analyzeProjectContent,
  scoreIncentiveForProject
};
